import React, { useCallback, useMemo } from "react";
import KC_Heading3 from "../../../../../designsystem/typography/KC-Heading/KC_Heading3";
import { KC_Weights } from "../../../../../designsystem/ds_commons";
import type BusinessUser from "@tootsweet/model/entities/BusinessUser";
import { Form, Formik } from "formik";
import { createPOSADeliveryAndBills } from "../../../../13_SaaS/api";
import { useDispatch, useSelector } from "react-redux";
import {
  useAccountToDisplaySelector,
  updateCustomerThunk
} from "../../../../../redux/actions/user";
import KC_Input from "../../../../../designsystem/KC_FormInput/KC_Input";
import KC_PhoneInput from "../../../../../designsystem/KC_PhoneInput/KC_PhoneInput";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import { tt } from "../../../../../utils/i18n/i18n";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../../designsystem/KC_Button/KC_Button";
import { useHistory } from "react-router-dom";
import { setFlyersOrderStep } from "../../../../../redux/saas/reducer";
import {
  posaDistributionQuantityInfo,
  SAAS_FLYERS_ORDER
} from "../../../../../utils/routes/routes";
import NiceModal from "@ebay/nice-modal-react";
import { getCountriesByLanguage } from "../../../../../utils/i18n/getCountriesByLang";
import ConfirmPaymentPLVOrder from "../../../../../components/modal/ConfirmPayment/ConfirmPaymentPLVOrder";
import type { RootStateI } from "../../../../../redux";
import { setLoading } from "../../../../../redux/actions/app";
import { updatePOSAOrderThunk } from "../../../../../redux/saas/thunks";
import POSAOrderBottomButtons from "./POSAOrderBottomButtons";
import KC_Padder from "../../../../../designsystem/KC_Padder/KC_Padder";
import PostPaymentModal from "../../../../../components/modal/ConfirmPayment/PostPaymentModal";
import ShippingAddressForm from "../../../../../components/ShippingAddressForm/ShippingAddressForm";
import { FLYERS_ORDER_STEP_PAYMENT } from "../../../../../utils/constants";
import { buildPathWithTrackingId } from "../../../../../utils/routing/routingUtils";
import { delivery_info_validation_schema } from "./delivery_info_validation_schema";

export default function InfoDelivering() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const currentPOSAOrder = useSelector(
    (state: RootStateI) => state.saas.currentPOSAOrder
  );

  const handleChangeBillingCountry = async (values: BusinessUser) => {
    if (accountToDisplay == null || !accountToDisplay.trackingID) {
      return;
    }

    await dispatch(
      updateCustomerThunk(
        values,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const handleNext = useCallback(
    async (values: BusinessUser) => {
      if (
        !values?.shippingAddress?.countryCode ||
        values?.shippingAddress.countryCode.toLowerCase() !== "fr"
      ) {
        void NiceModal.show(PostPaymentModal, {
          headerText: tt("cannot_order_posa_outside_of_france_title"),
          bodyText: tt("cannot_order_posa_outside_of_france_text"),
          buttonText: tt("ok")
        });
        return;
      }

      await dispatch(
        updateCustomerThunk(
          values,
          accountToDisplay?.trackingID || "",
          accountToDisplay?.token
        )
      );

      const confirmed = await openConfirmModal();

      if (!confirmed) {
        return;
      }

      dispatch(setLoading(true));

      const posaDeliveryWithBillResponse = await generateBill(values);

      if (!posaDeliveryWithBillResponse) {
        dispatch(setLoading(false));
        alert(tt("generic_error"));
        return;
      }

      /**
       * Go to payment step
       */
      dispatch(setFlyersOrderStep(FLYERS_ORDER_STEP_PAYMENT));
      history.push(
        buildPathWithTrackingId(
          SAAS_FLYERS_ORDER,
          accountToDisplay?.trackingID,
          false,
          {
            billId: posaDeliveryWithBillResponse[0]?.billId
          }
        )
      );
      dispatch(setLoading(false));
    },
    [accountToDisplay]
  );

  const openConfirmModal = async (): Promise<boolean> => {
    return await new Promise<boolean>((resolve) => {
      NiceModal.show(ConfirmPaymentPLVOrder, {
        resolveModal: resolve
      });
    });
  };

  const generateBill = async (bu: BusinessUser) => {
    if (
      bu?.shippingAddress == null ||
      !currentPOSAOrder?.items ||
      !accountToDisplay?.token ||
      !accountToDisplay?.trackingID
    ) {
      return null;
    }

    try {
      const posaDeliveryWithBillResponse = await createPOSADeliveryAndBills(
        accountToDisplay.trackingID,
        accountToDisplay.token,
        currentPOSAOrder.items
      );
      dispatch(
        updatePOSAOrderThunk({
          ...currentPOSAOrder,
          billAndDelivery: posaDeliveryWithBillResponse
        })
      );
      return posaDeliveryWithBillResponse;
    } catch (err) {
      console.error(err);
      alert(tt("api_error"));
    }
    return null;
  };

  const goBack = () => {
    history.push(
      buildPathWithTrackingId(
        posaDistributionQuantityInfo,
        accountToDisplay?.trackingID
      )
    );
  };

  const { ALL_COUNTRIES_NAME, ALL_COUNTRIES_CODE } = useMemo(() => {
    return getCountriesByLanguage();
  }, []);

  if (!accountToDisplay?.businessUser) {
    return null;
  }

  return (
    <div className="d-flex flex-column st-gap dashboard-v2">
      <div>
        <Formik<BusinessUser>
          initialValues={accountToDisplay?.businessUser}
          onSubmit={handleNext}
          enableReinitialize={true}
          validationSchema={delivery_info_validation_schema}
        >
          {({ values, setFieldValue, errors }) => {
            return (
              <Form className="d-flex flex-column st-gap">
                <div className="w-100 px-4">
                  <KC_Heading3 text={tt("info_delivering_billing_info")} />
                </div>
                <div className="w-100 d-flex flex-column flex-md-row st-gap px-4">
                  <div className="w-100">
                    <label htmlFor="firstName">
                      <KC_UIText
                        weight={KC_Weights.md}
                        text={tt("jp_onboarding_form_label_first_name")}
                      />
                    </label>
                    <KC_Input
                      id="firstName"
                      htmlFor="firstName"
                      placeholder={tt("jp_onboarding_form_label_first_name")}
                      type="text"
                      setFieldValue={setFieldValue}
                      required={true}
                      value={values?.firstName ? values.firstName : ""}
                    />
                  </div>
                  <div className="w-100">
                    <label htmlFor="lastName">
                      <KC_UIText
                        weight={KC_Weights.md}
                        text={tt("jp_onboarding_form_label_last_name")}
                      />
                    </label>
                    <KC_Input
                      id="lastName"
                      htmlFor="lastName"
                      placeholder={tt("jp_onboarding_form_label_last_name")}
                      type="text"
                      setFieldValue={setFieldValue}
                      required={true}
                      value={values?.lastName ? values.lastName : ""}
                    />
                  </div>
                </div>
                <div className="w-100 d-flex flex-column flex-md-row st-gap px-4">
                  <div className="w-100 flex-grow-1">
                    <label>
                      <KC_UIText
                        weight={KC_Weights.bold}
                        text={tt("info_delivering_company_optional")}
                      />
                    </label>
                    <KC_Input
                      id="companyName"
                      placeholder={tt(
                        "info_delivering_company_name_placeholder"
                      )}
                      htmlFor="companyName"
                      setFieldValue={setFieldValue}
                      required={false}
                    />
                  </div>
                  <div className="w-100 flex-grow-1">
                    <label>
                      <KC_UIText
                        weight={KC_Weights.bold}
                        text={tt("info_delivering_billing_country")}
                      />
                    </label>
                    <div className="kc-input-wrapper">
                      <select
                        id="billingCountry"
                        className="kc_input pl-3"
                        onChange={(e) => {
                          void handleChangeBillingCountry({
                            ...values,
                            billingCountry: e.target.value
                          });
                        }}
                        required={true}
                        disabled={false}
                      >
                        <React.Fragment>
                          <option>
                            {tt("info_delivering_billing_country_placeholder")}
                          </option>
                          {ALL_COUNTRIES_NAME.map((name, idx) => {
                            const val = ALL_COUNTRIES_CODE[idx];
                            return (
                              <option
                                key={`country-${idx}-${val}`}
                                value={val}
                                selected={
                                  !!accountToDisplay?.businessUser
                                    ?.billingCountry &&
                                  val ===
                                    accountToDisplay.businessUser
                                      ?.billingCountry
                                }
                              >
                                {name}
                              </option>
                            );
                          })}
                        </React.Fragment>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="w-100 px-4">
                  <label>
                    <KC_UIText
                      weight={KC_Weights.bold}
                      text={tt("info_delivering_billing_address")}
                    />
                  </label>
                  <KC_Input
                    id="companyAddress"
                    placeholder=""
                    htmlFor="companyAddress"
                    setFieldValue={setFieldValue}
                    required={true}
                  />
                </div>

                <div className="w-100 px-4 mt-4">
                  <KC_Heading3 text={tt("info_delivering_shipping_info")} />
                </div>
                <div className="w-100 d-flex flex-column flex-md-row st-gap px-4">
                  <div className="w-100 flex-grow-1">
                    <label>
                      <KC_UIText
                        weight={KC_Weights.bold}
                        text={tt("info_delivering_delivery_contact")}
                      />
                    </label>
                    <KC_Input
                      id="shippingAddress_name"
                      placeholder={tt(
                        "info_delivering_shipping_name_placeholder"
                      )}
                      htmlFor={"shippingAddress.name"}
                      setFieldValue={setFieldValue}
                      required={true}
                    />
                  </div>
                  <div className="w-100 flex-grow-1">
                    <label>
                      <KC_UIText
                        weight={KC_Weights.bold}
                        text={tt("info_delivering_phone_number")}
                      />
                    </label>
                    <KC_PhoneInput
                      id={"shippingAddress_pn"}
                      placeholder={tt(
                        "info_delivering_shipping_phone_placeholder"
                      )}
                      htmlFor={"shippingAddress.phoneNumber"}
                      setFieldValue={setFieldValue}
                      required={true}
                      errors={errors}
                      value={values?.shippingAddress?.phoneNumber || ""}
                      defaultCountry={
                        (values?.shippingAddress?.countryCode?.toLowerCase() as any) ||
                        "fr"
                      }
                    />
                  </div>
                </div>
                <div className="w-100 px-4">
                  <ShippingAddressForm
                    address={values.shippingAddress}
                    setFieldValue={setFieldValue}
                    fieldPrefix="shippingAddress"
                  />
                </div>
                <div className="d-block d-sm-none">
                  <KC_Padder />
                </div>
                <div
                  id="posa-footer"
                  className="d-flex justify-content-end border-top p-4"
                >
                  <POSAOrderBottomButtons
                    leftButton={
                      <KC_Button
                        isInverted={true}
                        size={KC_ButtonSize.Large}
                        text={tt("info_delivering_previous")}
                        type={KC_ButtonType.Button}
                        onClick={goBack}
                        className="d-flex"
                      />
                    }
                    rightButton={
                      <KC_Button
                        id={"info-delivering-next-btn"}
                        isPrimary={true}
                        size={KC_ButtonSize.Large}
                        type={KC_ButtonType.Submit}
                        text={tt("info_delivering_next")}
                        className="d-flex"
                      />
                    }
                  />
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
